/**
 * Web Vitals Reporting
 * Monitors and reports Core Web Vitals metrics for performance optimization
 * 
 * Metrics tracked:
 * - LCP (Largest Contentful Paint): < 2.5s = Good
 * - INP (Interaction to Next Paint): < 200ms = Good (replaced FID)
 * - CLS (Cumulative Layout Shift): < 0.1 = Good
 * - FCP (First Contentful Paint): < 1.8s = Good
 * - TTFB (Time to First Byte): < 800ms = Good
 */

import { onCLS, onINP, onLCP, onFCP, onTTFB, Metric } from 'web-vitals';
import { track } from '@vercel/analytics';

type MetricName = 'CLS' | 'INP' | 'LCP' | 'FCP' | 'TTFB';

interface WebVitalsMetric {
  name: MetricName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

/**
 * Get rating based on metric thresholds
 */
function getRating(metric: Metric): 'good' | 'needs-improvement' | 'poor' {
  const { name, value } = metric;
  
  // Thresholds based on Web Vitals recommendations
  const thresholds: Record<MetricName, { good: number; poor: number }> = {
    LCP: { good: 2500, poor: 4000 },      // milliseconds
    INP: { good: 200, poor: 500 },        // milliseconds (replaced FID)
    CLS: { good: 0.1, poor: 0.25 },       // unitless
    FCP: { good: 1800, poor: 3000 },      // milliseconds
    TTFB: { good: 800, poor: 1800 },      // milliseconds
  };
  
  const threshold = thresholds[name as MetricName];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Send metric to analytics
 */
function sendToAnalytics(metric: WebVitalsMetric) {
  // Log to console in development
  if (import.meta.env.DEV) {
    const emoji = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
    console.log(
      `${emoji} ${metric.name}: ${metric.value.toFixed(2)}${metric.name === 'CLS' ? '' : 'ms'} (${metric.rating})`
    );
  }

  // Send to Vercel Analytics via track() (v1.x API)
  track('Web Vitals', {
    metric: metric.name,
    value: Math.round(metric.value),
    rating: metric.rating,
  });

  // Send to Google Analytics (if available)
  if ((window as any).gtag) {
    (window as any).gtag('event', metric.name, {
      value: Math.round(metric.value),
      metric_id: metric.id,
      metric_value: metric.value,
      metric_delta: metric.delta,
      metric_rating: metric.rating,
    });
  }
}

/**
 * Report Web Vitals metrics
 */
export function reportWebVitals() {
  const handleMetric = (metric: Metric) => {
    const webVitalsMetric: WebVitalsMetric = {
      name: metric.name as MetricName,
      value: metric.value,
      rating: getRating(metric),
      delta: metric.delta,
      id: metric.id,
    };
    
    sendToAnalytics(webVitalsMetric);
  };

  // Register all Web Vitals listeners
  onCLS(handleMetric);
  onINP(handleMetric);
  onLCP(handleMetric);
  onFCP(handleMetric);
  onTTFB(handleMetric);
}

// Note: window.va is declared by @vercel/analytics — do not redeclare here

export default reportWebVitals;
